/**
 * Current-brand state. Brand-scoped routes live under /b/:brandId/*; the last brand is remembered
 * so global pages (chat, settings) and the rail know where "Home" goes.
 */
import { useEffect, useMemo } from "react";
import { ErrorState, PageSpinner } from "@/components/ui";
import { Navigate, Outlet, useParams } from "react-router";
import { useTool } from "@/api/hooks";
import { pickArray, type Brand } from "@/api/types";

const KEY = "mimic.lastBrand";

export function BrandScope() {
  const { brandId } = useParams();
  const { brands, isLoading, error, refetch, data } = useBrands();
  const available = brands.some((b) => b.id === brandId || b.slug === brandId);
  useEffect(() => {
    if (brandId && data && !error && !available) forgetBrand(brandId);
  }, [brandId, data, error, available]);
  if (isLoading) return <PageSpinner />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!brandId || !available) return <Navigate to="/brands" replace />;
  return <Outlet key={brandId} />;
}

export function rememberBrand(id: string | null | undefined): void {
  try {
    if (id) localStorage.setItem(KEY, id);
  } catch {
    /* private mode */
  }
}

export function forgetBrand(id: string): void {
  try { if (localStorage.getItem(KEY) === id) localStorage.removeItem(KEY); } catch { /* private mode */ }
}

export function lastBrand(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function useBrands() {
  const q = useTool<unknown>("brands.list", {}, { staleTime: 60_000 });
  const brands = useMemo(() => pickArray<Brand>(q.data, "brands").filter((b) => b.status !== "archived"), [q.data]);
  return { ...q, brands };
}

/** Brand id from the route, falling back to the remembered brand (for global pages). */
export function useBrandId(): string | null {
  const { brandId } = useParams();
  return brandId ?? lastBrand();
}

/** Route brand only (null on global pages / All brands mode). */
export function useRouteBrandId(): string | null {
  const { brandId } = useParams();
  return brandId ?? null;
}

export function useCurrentBrand() {
  const brandId = useBrandId();
  const { brands, isLoading, error } = useBrands();
  const brand = brands.find((b) => b.id === brandId || b.slug === brandId) ?? null;
  return { brandId: brand?.id ?? brandId, brand, brands, isLoading, error };
}

/** Build a brand-scoped path ("/b/:id/home"). Falls back to /brands when no brand is known. */
export function brandPath(brandId: string | null | undefined, page: string): string {
  if (!brandId) return page === "home" ? "/all" : "/brands";
  return `/b/${encodeURIComponent(brandId)}/${page.replace(/^\//, "")}`;
}
